import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import OtpInput from 'react-otp-input';
import { totpService } from '../../../../services/totpService';
import Modal from '../../../../components/ui/Modal/Modal.jsx';
import './TOTPSetupModal.css';

const TOTPSetupModal = ({ onSuccess, onCancel, onError }) => {
  const { t } = useTranslation(['profile', 'common']);
  const [step, setStep] = useState('loading'); // loading, setup, verify
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [manualEntryKey, setManualEntryKey] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Initialize TOTP setup
  useEffect(() => {
    const initializeSetup = async () => {
      try {
        setStep('loading');
        const response = await totpService.setup();
        setOtpauthUrl(response.otpauth_url);
        setManualEntryKey(response.manualEntryKey);
        setStep('setup');
      } catch (error) {
        console.error('TOTP setup error:', error);
        const errorMessage = error.response?.data?.message || t('profile:totp.setup_failed');
        setError(errorMessage);
        onError(errorMessage);
      }
    };

    initializeSetup();
  }, [t, onError]);

  const handleVerify = async () => {
    if (!otpValue || otpValue.length !== 6) {
      return; // Don't show error, just return silently
    }

    try {
      setLoading(true);
      setError('');
      
      await totpService.verify(otpValue);
      onSuccess();
    } catch (error) {
      console.error('TOTP verification error:', error);
      const errorMessage = error.response?.data?.message || t('profile:totp.verification_failed');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setStep('verify');
    setError('');
  };

  const handleBack = () => {
    setStep('setup');
    setError('');
    setOtpValue('');
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    });
  };

  const renderLoadingStep = () => (
    <div className="totp-loading-step">
      <div className="loading-spinner"></div>
      <p>{t('profile:totp.generating_qr')}</p>
    </div>
  );

  const renderSetupStep = () => (
    <div className="totp-setup-step">
      <p className="setup-description">
        {t('profile:totp.setup_description')}
      </p>

      <div className="qr-section">
        <div className="qr-code-container">
          <QRCodeSVG
            value={otpauthUrl}
            size={200}
            level="M"
            includeMargin={true}
          />
        </div>
        <p className="qr-instructions">
          {t('profile:totp.qr_instructions')}
        </p>
      </div>

      <div className="manual-entry-section">
        <h4>{t('profile:totp.manual_entry_title')}</h4>
        <p className="manual-instructions">
          {t('profile:totp.manual_entry_description')}
        </p>
        <div className="manual-key-container">
          <code className="manual-key">{manualEntryKey}</code>
          <button
            type="button"
            onClick={() => copyToClipboard(manualEntryKey)}
            className="copy-button"
            title={t('common:buttons.copy')}
          >
            📋
          </button>
        </div>
      </div>
    </div>
  );

  const renderVerifyStep = () => (
    <div className="totp-verify-step">
      <p className="verify-description">
        {t('profile:totp.verify_description')}
      </p>

      <div className="otp-input-container">
        <OtpInput
          value={otpValue}
          onChange={(value) => {
            setOtpValue(value);
            // Clear errors when user starts typing
            if (value.length > 0) {
              setError('');
            }
          }}
          numInputs={6}
          separator={<span className="otp-separator">-</span>}
          inputType="number"
          shouldAutoFocus={true}
          inputStyle={{
            width: '45px',
            height: '50px',
            fontSize: '1.5rem',
            textAlign: 'center',
            border: '2px solid #dee2e6',
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.2s ease',
            backgroundColor: '#fff'
          }}
          containerStyle={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px'
          }}
          renderInput={(props) => <input {...props} />}
        />
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );

  // Get the appropriate title based on the current step
  const getModalTitle = () => {
    switch (step) {
      case 'loading':
        return t('profile:totp.setup_title');
      case 'setup':
        return t('profile:totp.setup_title');
      case 'verify':
        return t('profile:totp.verify_title');
      default:
        return '';
    }
  };

  // Get the footer buttons based on the current step
  const getModalFooter = () => {
    if (step === 'loading') return null;

    if (step === 'setup') {
      return (
        <>
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="btn btn-primary"
          >
            {t('profile:totp.next_button')}
          </button>
        </>
      );
    }

    if (step === 'verify') {
      return (
        <>
          <button
            type="button"
            onClick={handleBack}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common:buttons.back')}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            type="button"
            onClick={handleVerify}
            className="btn btn-primary"
            disabled={loading || otpValue.length !== 6}
          >
            {loading ? t('profile:totp.verifying') : t('profile:totp.verify_button')}
          </button>
        </>
      );
    }

    return null;
  };

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={getModalTitle()}
      footer={getModalFooter()}
      size="medium"
    >
      {step === 'loading' && renderLoadingStep()}
      {step === 'setup' && renderSetupStep()}
      {step === 'verify' && renderVerifyStep()}
    </Modal>
  );
};

export default TOTPSetupModal;